import React, { useEffect, useState, useCallback } from 'react'
import { InputFieldSet } from '../../components/common/InputFieldSet'
import { FieldGroupWrapper } from '../../style/StyledElement'
import SpinnerButton from '../../components/common/SpinnerButton'
import AgGridWrapper from '../../components/common/AgGridWrapper'
import { apiConfig } from '../../api/ApiConfig'
import { admetHeaders } from '../../configs/GridConfig'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Divider from '@mui/material/Divider'
import Button from '@mui/material/Button'
import DownloadIcon from '@mui/icons-material/Download'
import { styled } from '@mui/material/styles'
import { admetParams } from '../../configs/ADMETParameters'
import { useSelector } from 'react-redux'

const { getAdmet, logUserEvent } = apiConfig()

// Constants moved outside component to prevent recreation on each render
const SEARCH_STATES = {
  INIT: 'INIT',
  LOADING: 'LOADING',
  COMPLETE: 'COMPLETE',
  ERROR: 'ERROR',
  SUBMITTED: 'SUBMITTED',
}

// Styled components for professional appearance
const HeaderContainer = styled(Paper)(({ theme }) => ({
  background: 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)',
  color: '#FFFFFF',
  padding: theme.spacing(3, 4),
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(3),
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
}))

const ContentContainer = styled(Paper)(({ theme }) => ({
  background: '#FFFFFF',
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
  border: '1px solid #E5E7EB',
}))

const SearchSection = styled(Box)(({ theme }) => ({
  background: '#F9FAFB',
  padding: theme.spacing(3),
  borderRadius: theme.spacing(1.5),
  border: '1px solid #E5E7EB',
  marginBottom: theme.spacing(3),
}))

const StyledFieldGroupWrapper = styled(FieldGroupWrapper)({
  marginBottom: 0,
})

export const AdmetSearch = ({ smilesInput, data }) => {
  const [searchState, setSearchState] = useState(SEARCH_STATES.INIT)
  const [smiles, setSmiles] = useState(smilesInput || '')
  const [searchResult, setSearchResult] = useState([])
  const [downloadStatus, setDownloadStatus] = useState(false)
  const { userId, isTokenAuth } = useSelector((state) => state.auth)

  const handleDownloadCsv = () => {
    setDownloadStatus(true)
  }

  // Memoized search function to prevent unnecessary re-renders
  const search = useCallback(async () => {
    if (!smiles?.trim()) {
      return false
    }

    setSearchState(SEARCH_STATES.LOADING)

    // Log event
    if (userId) {
      logUserEvent({
        userId,
        eventType: 'ACTION',
        eventName: 'ANALYZE_ADMET',
        metadata: {
          smiles: smiles.trim(),
          token_auth: isTokenAuth || false
        },
        timestamp: new Date().toISOString()
      })
    }
    
    try {
      const input = { smiles: smiles.trim() }
      const response = await getAdmet(JSON.stringify(input))
      
      if (!response) {
        setSearchState(SEARCH_STATES.ERROR)
        return false
      }

      if (response.status) {
        setSearchResult([])
        setSearchState(SEARCH_STATES.SUBMITTED)
      } else {
        const resultList = Object.entries(response)
          .filter(([key]) => admetParams[key]) // Only process known parameters
          .map(([key, value]) => {
            const paramConfig = admetParams[key]
            return {
              parameter: key,
              value: value,
              siunit: paramConfig.siunit || '',
              stdvalue: paramConfig.stdvalue || '',
              significance: paramConfig.significance || '',
            }
          })

        setSearchResult(resultList)
        setSearchState(SEARCH_STATES.COMPLETE)
      }
      
      return true
    } catch (error) {
      console.error('ADMET search error:', error)
      setSearchState(SEARCH_STATES.ERROR)
      setSearchResult([])
      return false
    }
  }, [smiles])

  // Handle prop changes more efficiently
  useEffect(() => {
    if (smilesInput !== undefined && smilesInput !== smiles) {
      setSmiles(smilesInput)
    }
  }, [smilesInput, smiles])

  useEffect(() => {
    if (data && Array.isArray(data)) {
      setSearchResult(data)
      setSearchState(SEARCH_STATES.COMPLETE)
    }
  }, [data])

  // Handle SMILES input change
  const handleSmilesChange = useCallback((value) => {
    setSmiles(value)
    // Reset search state when input changes
    if (searchState !== SEARCH_STATES.INIT) {
      setSearchState(SEARCH_STATES.INIT)
    }
  }, [searchState])

  const isLoading = searchState === SEARCH_STATES.LOADING
  const hasError = searchState === SEARCH_STATES.ERROR
  const isSubmitted = searchState === SEARCH_STATES.SUBMITTED

  return (
    <div>
      {/* Professional Header */}
      <HeaderContainer elevation={0}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                fontWeight: 600, 
                marginBottom: 1,
                fontSize: { xs: '1.5rem', sm: '2rem' }
              }}
            >
              ADMET Analysis
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                opacity: 0.9,
                fontSize: '1.1rem',
                lineHeight: 1.5
              }}
            >
              Absorption, Distribution, Metabolism, Excretion, and Toxicity Prediction
            </Typography>
          </Box>
          <Box sx={{ 
            display: { xs: 'none', md: 'block' },
            opacity: 0.3,
            fontSize: '4rem',
            fontWeight: 300
          }}>
            🧬
          </Box>
        </Box>
      </HeaderContainer>

      {/* Main Content */}
      <ContentContainer elevation={0}>
        {/* Search Section */}
        <SearchSection>
          <Typography 
            variant="h6" 
            sx={{ 
              marginBottom: 2, 
              color: '#374151',
              fontWeight: 600
            }}
          >
            Molecular Structure Input
          </Typography>
          <Divider sx={{ marginBottom: 3, backgroundColor: '#E5E7EB' }} />
          
          <StyledFieldGroupWrapper>
            <Box sx={{ display: 'flex', alignItems: 'end', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <InputFieldSet
                  name="Smiles"
                  value={smiles}
                  mandatory={false}
                  label="SMILES Notation"
                  onChange={handleSmilesChange}
                  disabled={isLoading}
                />
              </Box>
              <Box sx={{ paddingBottom: 0.5 }}>
                <SpinnerButton
                  label="Analyze"
                  className="btn-primary"
                  onClick={search}
                  disabled={!smiles?.trim() || isLoading}
                  loading={isLoading}
                  sx={{
                    background: 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #4B5563 0%, #374151 100%)',
                    },
                    minHeight: '42px',
                    fontWeight: 600,
                    textTransform: 'none',
                    letterSpacing: '0.5px'
                  }}
                />
              </Box>
            </Box>
          </StyledFieldGroupWrapper>
        </SearchSection>

        {/* Results Section */}
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                color: '#374151',
                fontWeight: 600
              }}
            >
              Analysis Results
            </Typography>
            {searchResult && searchResult.length > 0 && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadCsv}
                sx={{
                  borderColor: '#10B981',
                  color: '#059669',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: '#059669',
                    backgroundColor: 'rgba(16, 185, 129, 0.08)',
                  }
                }}
              >
                Download CSV Table
              </Button>
            )}
          </Box>
          <Divider sx={{ marginBottom: 2, backgroundColor: '#E5E7EB' }} />
          
          <AgGridWrapper
            data={searchResult}
            headers={admetHeaders}
            loading={isLoading}
            error={hasError}
            submitted={isSubmitted}
            api={search}
            downloadStatus={downloadStatus}
            setDownloadStatus={setDownloadStatus}
          />
        </Box>
      </ContentContainer>
    </div>
  )
}
